import type { GitInfo } from "features/types/sandbox";
import type { IconType } from "prism-react";
import { Badge, Tooltip } from "prism-react";

import { paths } from "utils/router";
export const EditorType: React.FC<{
  type: EditorTypeProps;
}> = ({ type }) => {
  const { icon, tooltipText, editorName } = getContextType(type);

  return (
    <Tooltip content={tooltipText}>
      <div>
        <Badge icon={icon}>{editorName}</Badge>
      </div>
    </Tooltip>
  );
};

export type EditorTypeProps =
  | {
      type: "devbox" | "sandbox";
      iconUrl?: string | undefined;
      templateName?: string;
      git?: GitInfo | null;
      workspaceId?: string;
    }
  | {
      type: "repository";
      owner: string;
      repo: string;
    }
  | {
      type: "contribution";
    };

const getContextType = (
  props: EditorTypeProps,
): {
  icon: IconType;
  tooltipText: React.ReactNode;
  editorName: string;
} => {
  if (props.type === "devbox") {
    let editorName = "Devbox";
    let tooltipText: React.ReactNode =
      "Devboxes run your code in our Cloud Development Environment, so you can build any project without limits";

    if (props.git) {
      editorName = "Synced Devbox";
      tooltipText = (
        <span>
          Synced Devboxes are Devbox templates that stay in sync with GitHub
          branches{" "}
          <a
            href="https://codesandbox.io/docs/learn/vm-sandboxes/synced-templates"
            rel="noreferrer noopener"
            target="_blank"
          >
            Learn more.
          </a>
        </span>
      );
    } else if (props.templateName) {
      editorName = "Devbox template";
    }

    return { icon: "server", editorName, tooltipText };
  }

  if (props.type === "sandbox") {
    return {
      icon: "sandbox",
      editorName: props.templateName ? "Sandbox template" : "Sandbox",
      tooltipText: (
        <span>
          Sandboxes run your code in the browser. You can revert to the old
          editor by accessing the{" "}
          <a href={paths.dashboard.experiments(props.workspaceId)}>
            preferences
          </a>
        </span>
      ),
    };
  }

  return {
    icon: "github",
    editorName: "Repository",
    tooltipText:
      "This branch is connected to a GitHub repository and running on our Cloud Development Environment",
  };
};
